import { memo, useMemo } from 'react'
import type { SlideshowPreview } from '../../lib/infoserviceSlideshowApi.js'
import { formatClock, formatSpan, stepLabels, timelineTicks } from '../../lib/slideshowSettingsForm.js'

interface SlideshowTimelineProps {
  preview: SlideshowPreview
  hasCg: boolean
}

const pct = (value: number, total: number) => `${(value / total) * 100}%`

/**
 * Frise d'une plage telle que le serveur la tirera : piste vidéo (intro,
 * annonces, outro), piste habillage, piste musique. Les instants sont ceux
 * du plan, sans recalcul côté écran.
 */
export const SlideshowTimeline = memo(function SlideshowTimeline({ preview, hasCg }: SlideshowTimelineProps) {
  const { plan } = preview
  const total = Math.max(1, plan.durationMs)
  const labels = useMemo(() => stepLabels(plan), [plan])
  const ticks = useMemo(() => timelineTicks(total), [total])
  const tourStarts = useMemo(
    () => plan.steps.filter((step, index) =>
      step.kind === 'ANNOUNCEMENT' && step.tour > 1 && plan.steps[index - 1]?.kind === 'ANNOUNCEMENT'
      && (plan.steps[index - 1] as { tour: number }).tour !== step.tour),
    [plan],
  )

  if (plan.status !== 'SLIDESHOW') {
    return (
      <div className="slideshow-timeline__empty">
        {plan.status === 'NO_ANNOUNCEMENT'
          ? 'Aucune annonce diffusable ce jour-là : la plage passerait sur l’interlude, sans intro.'
          : 'Plage plus courte que l’intro : rien ne serait posé.'}
      </div>
    )
  }

  return (
    <div className="slideshow-timeline" role="img" aria-label={`Déroulé d’une plage de ${formatSpan(total)}`}>
      <div className="slideshow-timeline__ruler" aria-hidden="true">
        <span />
        <div className="slideshow-timeline__scale">
          {ticks.map(at => (
            <span key={at} className="slideshow-timeline__tick" style={{ left: pct(at, total) }}>{formatClock(at)}</span>
          ))}
          <span className="slideshow-timeline__tick is-end" style={{ left: '100%' }}>{formatClock(total)}</span>
          {tourStarts.map(step => (
            <span key={`tour-${step.startMs}`} className="slideshow-timeline__tour" style={{ left: pct(step.startMs, total) }}>
              la liste reprend
            </span>
          ))}
        </div>
      </div>

      <div className="slideshow-timeline__track">
        <span className="slideshow-timeline__track-label">Vidéo</span>
        <div className="slideshow-timeline__lane">
          {plan.steps.map(step => {
            const width = pct(step.endMs - step.startMs, total)
            const left = pct(step.startMs, total)
            const seconds = Math.round((step.endMs - step.startMs) / 1000)
            if (step.kind !== 'ANNOUNCEMENT') {
              return (
                <span key={`${step.kind}-${step.startMs}`} className={`slideshow-timeline__block is-${step.kind.toLowerCase()}`} style={{ left, width }}
                  title={`${step.kind === 'INTRO' ? 'Intro' : 'Outro'} · ${seconds} s`}>
                  <strong>{step.kind === 'INTRO' ? 'Intro' : 'Outro'}</strong>
                  <small>{seconds} s</small>
                </span>
              )
            }
            const info = preview.announcements[step.announcementId]
            const name = info ? [info.organizationLabel, info.eventLabel].filter(Boolean).join(' — ') : step.announcementId
            return (
              <span
                key={`${step.announcementId}-${step.startMs}`}
                className={[
                  'slideshow-timeline__block',
                  step.priority ? 'is-priority' : 'is-announcement',
                  step.prolonged ? 'is-prolonged' : '',
                  step.cut ? 'is-cut' : '',
                ].filter(Boolean).join(' ')}
                style={{ left, width }}
                title={`${name} · ${formatClock(step.startMs)} → ${formatClock(step.endMs)}${step.prolonged ? ' · prolongée jusqu’à la coupure' : ''}${step.cut ? ' · coupée par la fin de plage' : ''}`}
              >
                <strong>{labels.get(step.announcementId)}</strong>
                <small>{seconds} s{step.prolonged ? ' ↦' : ''}</small>
              </span>
            )
          })}
        </div>
      </div>

      <div className="slideshow-timeline__track">
        <span className="slideshow-timeline__track-label">Habillage</span>
        <div className="slideshow-timeline__lane is-thin">
          {hasCg
            ? (
                <>
                  {plan.cgVisibleFromMs > 0 && (
                    <span className="slideshow-timeline__band is-hidden" style={{ left: 0, width: pct(plan.cgVisibleFromMs, total) }}>masqué</span>
                  )}
                  <span className="slideshow-timeline__band is-cg" style={{ left: pct(plan.cgVisibleFromMs, total), width: pct(total - plan.cgVisibleFromMs, total) }}>
                    Habillage d’infoservice à l’écran{plan.cgVisibleFromMs > 0 ? ' — masqué pendant l’intro' : ''}
                  </span>
                </>
              )
            : <span className="slideshow-timeline__band is-hidden" style={{ left: 0, width: '100%' }}>Aucun habillage choisi</span>}
        </div>
      </div>

      <div className="slideshow-timeline__track">
        <span className="slideshow-timeline__track-label">Musique</span>
        <div className="slideshow-timeline__lane is-thin">
          {plan.music
            ? (
                <span className="slideshow-timeline__band is-music" style={{ left: pct(plan.music.startMs, total), width: pct(plan.music.endMs - plan.music.startMs, total) }}>
                  <span className="slideshow-timeline__fade is-in" style={{ width: pct(plan.music.fadeInMs, plan.music.endMs - plan.music.startMs) }} aria-hidden="true" />
                  Playlist — reprend où la plage précédente s’est arrêtée
                  <span className="slideshow-timeline__fade is-out" style={{ width: pct(plan.music.endMs - plan.music.fadeOutStartMs, plan.music.endMs - plan.music.startMs) }} aria-hidden="true" />
                </span>
              )
            : <span className="slideshow-timeline__band is-hidden" style={{ left: 0, width: '100%' }}>{preview.hasMusic ? 'Musique coupée' : 'Aucun morceau dans la playlist'}</span>}
        </div>
      </div>

      <footer className="slideshow-timeline__legend">
        <span><i className="is-intro" aria-hidden="true" /> Intro vidéo</span>
        <span><i className="is-priority" aria-hidden="true" /> Annonce prioritaire</span>
        <span><i className="is-announcement" aria-hidden="true" /> Annonce</span>
        <span><i className="is-prolonged" aria-hidden="true" /> Dernière annonce prolongée jusqu’à la coupure</span>
        <span className="slideshow-timeline__legend-end">Le bloc coupe sec à la fin de la plage</span>
      </footer>
    </div>
  )
})
